import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Button,
  Avatar,
  useTheme,
  Tooltip,
  Collapse,
  Stack,
  Divider,
  Badge,
  Typography,
} from '@mui/material';
import {
  PRIORITIES_ACTIVITIES_COLORS,
  PRIORITIES_ACTIVITIES_ICONS,
} from '../Utils/prioritiesActivities';
import TimerChip from '../Components/TimerChip';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import Chip from '../../../Components/Chip';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import {
  CenteredFlexBox,
  ColumnFlexBox,
  FlexBox,
  FullSizeCenteredFlexBox,
} from '../../../Components/FlexBox';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  activityTypesAtom,
  selectedBranchAtom,
  userBranchesAtom,
  selectedCustomerIdAtom,
  additionalCustomerDataAtom,
  openSnackbarAdviseAtom,
} from '../../../Atoms';
import useAPI from '../../../api';
import EntryComponent from '../../../Components/ContactListWrapper/EntryComponent';
import IconButton from '../../../Components/Button/IconButton';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import FmdBadOutlinedIcon from '@mui/icons-material/FmdBadOutlined';
import capitalize from '../../../Helpers/StringsFormatting/capitalize';
import { formatListItemDate } from '../../../Helpers/dates';
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';

const ActivityEntryItem = function ActivityEntryItem({ data }) {
  const API = useAPI();
  const intl = useIntl();
  const theme = useTheme();

  const openSnackbar = useSetAtom(openSnackbarAdviseAtom);
  const setCustomerId = useSetAtom(selectedCustomerIdAtom);
  const setAdditionalCustomerData = useSetAtom(additionalCustomerDataAtom);
  const activityTypes = useAtomValue(activityTypesAtom);
  const userBranches = useAtomValue(userBranchesAtom);
  const selectedBranch = useAtomValue(selectedBranchAtom);

  const [expanded, setExpanded] = useState(false);
  const [participantId, setParticipantId] = useState(null);

  const toggleExpanded = (e) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const handleMakeCallWithoutContact = () => {
    if (!data?.phoneNumber) {
      openSnackbar({
        message: intl.messages.PHONE_NUMBER_UNAVAILABLE,
        severity: 'error',
      });
      return;
    }
    API.makeCallWithoutContact(
      data?.phoneNumber,
      (res) => {
        setParticipantId(res);
      },
      () => {
        openSnackbar({
          message: intl.messages.CALLSTART_ERROR,
          severity: 'error',
        });
      }
    );
  };

  const handleDropCallWithoutContact = () => {
    API.dropCallWithoutContact(
      participantId,
      () => {
        setParticipantId(null);
      },
      () => {
        openSnackbar({
          message: intl.messages.CALLDROP_ERROR,
          severity: 'error',
        });
      }
    );
  };

  const customChipCallback = ({ data }) => (
    <CenteredFlexBox justifyContent="space-between" gap="4px">
      <Button
        id={`manage-btn-${data?.id}`}
        size="large"
        sx={{ textTransform: 'none', width: '160px', flexShrink: 0 }}
        disableElevation
        variant={!data?.contactId ? 'outlined' : 'contained'}
        color={participantId ? 'error' : 'primary'}
        onClick={(e) => {
          e.stopPropagation();
          if (participantId) {
            handleDropCallWithoutContact();
            return;
          }
          if (!data?.contactId) {
            handleMakeCallWithoutContact();
            return;
          }
          setCustomerId(data?.contactId);
          setAdditionalCustomerData({
            customerName: data?.fullName,
            parentId: data?.activityId,
            activityTypeId: data?.activityType,
            outcomesFilter: 'RECALL',
            sectionId: 'PRIORITY',
            branchId: data?.assignedBranchId ?? undefined,
          });
        }}
        fullWidth
        startIcon={!data?.contactId ? <LocalPhoneRoundedIcon /> : null}
      >
        {data?.contactId
          ? intl.messages.MANAGE
          : participantId
            ? intl.messages.DROP_CALL
            : intl.messages.CALL_CONTACT}
      </Button>
      {data?.recallCount > 0 && data?.recalls ? (
        <IconButton
          id={`expand-icn-btn-${data?.id}`}
          size="large"
          onClick={toggleExpanded}
        >
          {expanded ? (
            <KeyboardArrowUpRoundedIcon />
          ) : (
            <KeyboardArrowDownRoundedIcon />
          )}
        </IconButton>
      ) : (
        <div style={{ width: '48px', height: '48px' }} />
      )}
    </CenteredFlexBox>
  );
  const columns = [
    {
      id: 'activityType',
      noCaption: true,
      size: 0.5,
      formatCallback: (value, data) => {
        const activityTypeName =
          activityTypes.find((type) => type.id === value)?.name || value;
        const Icon =
          PRIORITIES_ACTIVITIES_ICONS[
            activityTypeName === 'CALL' ? 'RECALL' : activityTypeName
          ];
        return Icon ? (
          <Badge
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            badgeContent={
              data?.attempts ? (
                <Tooltip
                  title={intl.messages.ATTEMPTS}
                  arrow
                  placement="bottom"
                >
                  <Typography>{data?.attempts}</Typography>
                </Tooltip>
              ) : null
            }
            color="primary"
          >
            <Avatar
              sx={{
                width: '46px',
                height: '46px',
                fontSize: '1.25rem',
                cursor: 'pointer',
                ...theme.custom.bgAndColorStyle(
                  PRIORITIES_ACTIVITIES_COLORS[activityTypeName],
                  theme.palette.mode
                ),
              }}
            >
              <Tooltip
                title={intl.messages[activityTypeName] ?? activityTypeName}
                arrow
                placement="bottom"
              >
                <Icon sx={{ color: 'inherit', fontSize: 'inherit' }} />
              </Tooltip>
            </Avatar>
          </Badge>
        ) : (
          value
        );
      },
    },
    {
      id: 'fullName',
      noCaption: true,
      formatCallback: (value, data) => (
        <FullSizeCenteredFlexBox gap="8px" justifyContent="space-between">
          <Typography variant="body1" fontWeight="bold" flexGrow={1}>
            {capitalize(value) ?? intl.messages.CONTACT_UNAVAILABLE}
          </Typography>
          {data?.assignedBranchId &&
          data?.assignedBranchId !== data?.branchId ? (
            <Tooltip
              title={`${intl.messages.ASSIGNEDBRANCH}: ${data?.assignedBranch}`}
              placement="top"
              arrow
              slotProps={{
                tooltip: { sx: { fontSize: '1rem', padding: '8px' } },
              }}
            >
              <FmdBadOutlinedIcon color="warning" />
            </Tooltip>
          ) : null}
        </FullSizeCenteredFlexBox>
      ),
    },
    /*     {
      id: 'assignedBranch',
      size: 1.5,
      formatCallback: (value, data) =>
        data?.assignedBranchId !== data?.branchId ? value : null,
    }, */
    {
      id: 'phoneNumber',
      formatCallback: (value) =>
        userBranches.length > 1 && selectedBranch && value ? (
          <a
            style={{
              color: 'inherit',
              fontFamily: 'Roboto',
              fontWeight: 600,
              fontSize: '0.95rem',
              letterSpacing: '0.02em',
              textDecoration: 'none',
            }}
            href={`tel:${selectedBranch.prefix3CX}${value.replace('+39', '')}`}
          >
            {value}
          </a>
        ) : (
          value
        ),
    },
    {
      id: 'activityDateTime',
      label: 'DATETIME',
      formatCallback: (value) =>
        value ? formatListItemDate(value, true) : null,
    },
    {
      id: 'lastRecallDateTime',
      label: 'LAST_RECALL_DATETIME',
      formatCallback: (value) =>
        value ? formatListItemDate(value, true) : null,
    },
    {
      id: 'lastActivityTime',
      size: 2.2,
      noCaption: true,
      formatCallback: (_, data) => (
        <TimerChip
          dateTime={data?.lastRecallDateTime ?? data?.activityDateTime}
          id={data?.id}
        />
      ),
    },
    {
      id: 'recallCount',
      noCaption: true,
      size: 0.8,
      formatCallback: (value, data) => {
        if (
          data?.activityType ===
          activityTypes.find((type) => type.name === 'SMS')?.id
        ) {
          return (
            <FlexBox justifyContent="center">
              <Tooltip
                id={`sms-content-${data?.fullName}`}
                title={data?.activityNote}
                slotProps={{
                  tooltip: { sx: { fontSize: '1rem', padding: '8px' } },
                }}
                placement="top"
                arrow
              >
                <MessageOutlinedIcon color="primary" />
              </Tooltip>
            </FlexBox>
          );
        }
        if (
          data?.activityType ===
            activityTypes.find((type) => type.name === 'MISSEDCALL')?.id &&
          value > 0
        ) {
          return (
            <FlexBox justifyContent="center">
              <Chip
                id={`recall-count-${data?.fullName}`}
                label={value}
                fontSize="1.2rem"
                icon={<PhoneOutlinedIcon sx={{ color: '#faac0c!important' }} />}
                color="#faac0c"
                labelColor="#111"
                width="64px"
              />
            </FlexBox>
          );
        }
        return null;
      },
    },
  ];
  return (
    <ColumnFlexBox>
      <EntryComponent
        data={data}
        columns={columns}
        customStatusChipCallback={customChipCallback}
        disableClickRow={true}
        hideDivider
      />
      <Collapse in={expanded} timeout="auto">
        <Stack
          sx={{
            gap: '16px',
            padding: '36px',
          }}
        >
          {data?.recalls?.map((recall) => (
            <EntryComponent
              key={recall?.recalledDateTime}
              data={recall}
              columns={[
                {
                  id: 'activityType',
                  noCaption: true,
                  size: 1,
                  formatCallback: () => {
                    const Icon = PRIORITIES_ACTIVITIES_ICONS['CALL'];
                    return Icon ? (
                      <Avatar
                        sx={{
                          width: '46px',
                          height: '46px',
                          fontSize: '1.25rem',
                          ...theme.custom.bgAndColorStyle(
                            PRIORITIES_ACTIVITIES_COLORS['CALL'],
                            theme.palette.mode
                          ),
                        }}
                      >
                        <Icon sx={{ color: 'inherit', fontSize: 'inherit' }} />
                      </Avatar>
                    ) : null;
                  },
                },
                { id: 'operatorName' },
                {
                  id: 'recallDateTime',
                  label: 'DATE',
                  formatCallback: (value) =>
                    value
                      ? new Date(value).toLocaleDateString(intl.locale, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : value,
                },
                {
                  id: 'recallDateTime',
                  label: 'TIME',
                  formatCallback: (value) =>
                    value
                      ? new Date(value).toLocaleTimeString(intl.locale, {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : value,
                },
              ]}
              customStatusChipCallback={() => null}
              disableClickRow={true}
              hideDivider
            />
          ))}
        </Stack>
      </Collapse>
      <Divider />
    </ColumnFlexBox>
  );
};

export default ActivityEntryItem;
